import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  useGetExpensesQuery,
  useCreateExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpenseAccountsQuery,
  useCreateAccountMutation,
} from '../../../store/api/financeApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';
import { useGetPaymentAccountsQuery } from '../../../store/api/purchaseApi';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

function Expenses() {
  const today = new Date().toISOString().split('T')[0];
  const [fromDate, setFromDate] = useState(today.slice(0, 7) + '-01');
  const [toDate, setToDate] = useState(today);
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data: expenses, isLoading, error } = useGetExpensesQuery({ fromDate, toDate });
  const { data: paymentAccounts } = useGetPaymentAccountsQuery();
  const { data: expenseAccounts } = useGetExpenseAccountsQuery();
  const [createExpense, { isLoading: saving }] = useCreateExpenseMutation();
  const [deleteExpense] = useDeleteExpenseMutation();
  const [createAccount, { isLoading: creatingAccount }] = useCreateAccountMutation();

  const [form, setForm] = useState({
    expenseDate: today,
    accountId: '',
    amount: '',
    paymentMethod: 'cash',
    paymentAccountId: '',
    referenceNo: '',
    notes: '',
  });

  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [newAccount, setNewAccount] = useState({ code: '', name: '', type: 'expense' });

  const sortedExpenseAccounts = useMemo(() => {
    return Array.isArray(expenseAccounts)
      ? [...expenseAccounts].sort((a, b) => String(a.code || '').localeCompare(String(b.code || '')))
      : [];
  }, [expenseAccounts]);

  const formatAmount = (value) => {
    const n = Number(value || 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(n);
    } catch {
      return `${currency} ${n.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = Number(form.amount || 0);
    if (!form.expenseDate || !form.accountId || !amt || amt <= 0) {
      alert('Date, expense account and amount are required.');
      return;
    }
    try {
      await createExpense({
        ...form,
        amount: amt,
        paymentAccountId: form.paymentAccountId || undefined,
        referenceNo: form.referenceNo || undefined,
        notes: form.notes || undefined,
      }).unwrap();
      setForm((prev) => ({
        ...prev,
        amount: '',
        referenceNo: '',
        notes: '',
      }));
    } catch (err) {
      alert(err?.data?.message || 'Error creating expense');
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 3 }}>
        Expenses
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <TextField
          type="date"
          label="From"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
        <TextField
          type="date"
          label="To"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          size="small"
        />
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Add Expense
          </Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}
          >
            <TextField
              type="date"
              label="Date *"
              value={form.expenseDate}
              onChange={(e) => setForm((prev) => ({ ...prev, expenseDate: e.target.value }))}
              InputLabelProps={{ shrink: true }}
              size="small"
            />
            <TextField
              select
              label="Expense Account *"
              value={form.accountId}
              onChange={(e) => setForm((prev) => ({ ...prev, accountId: e.target.value }))}
              size="small"
              sx={{ minWidth: 260 }}
            >
              <MenuItem value="">Select expense account</MenuItem>
              {sortedExpenseAccounts.map((acc) => (
                  <MenuItem key={acc.id} value={acc.id}>
                    {acc.code} - {acc.name}
                  </MenuItem>
                ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button variant="outlined" onClick={() => setAddAccountOpen(true)}>
                Add Expense Account
              </Button>
            </Box>
            <TextField
              type="number"
              label={`Amount (${currency}) *`}
              value={form.amount}
              onChange={(e) => setForm((prev) => ({ ...prev, amount: e.target.value }))}
              size="small"
              inputProps={{ min: 0, step: 0.01 }}
            />
            <TextField
              select
              label="Method *"
              value={form.paymentMethod}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  paymentMethod: e.target.value,
                }))
              }
              size="small"
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
            </TextField>
            <TextField
              select
              label="Payment Account (optional)"
              value={form.paymentAccountId}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, paymentAccountId: e.target.value }))
              }
              size="small"
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Auto (by method)</MenuItem>
              {paymentAccounts?.map((acc) => (
                <MenuItem key={acc.id} value={acc.id}>
                  {acc.code} - {acc.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Reference"
              value={form.referenceNo}
              onChange={(e) => setForm((prev) => ({ ...prev, referenceNo: e.target.value }))}
              size="small"
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Notes"
              value={form.notes}
              onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
              size="small"
              sx={{ minWidth: 260, flex: 1 }}
              multiline
              maxRows={3}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? 'Saving...' : 'Save Expense'}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Expenses List
          </Typography>
          {isLoading && <Typography>Loading...</Typography>}
          {error && (
            <Typography color="error">
              Error loading expenses
            </Typography>
          )}
          {!isLoading && !error && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Expense Account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Payment Account</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Reference</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {Array.isArray(expenses) && expenses.length > 0 ? (
                    expenses.map((exp) => (
                      <TableRow key={exp.id}>
                        <TableCell>
                          {exp.expenseDate
                            ? new Date(exp.expenseDate).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {exp.account
                            ? `${exp.account.code} - ${exp.account.name}`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {exp.paymentAccount
                            ? `${exp.paymentAccount.code} - ${exp.paymentAccount.name}`
                            : '-'}
                        </TableCell>
                        <TableCell>{exp.referenceNo || '-'}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>
                          {formatAmount(exp.amount)}
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this expense?')) {
                                try {
                                  await deleteExpense(exp.id).unwrap();
                                } catch (err) {
                                  alert(err?.data?.message || 'Error deleting expense');
                                }
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        <Typography color="text.secondary">
                          No expenses found for this period.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={addAccountOpen} onClose={() => setAddAccountOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Account</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Account Code"
              value={newAccount.code}
              onChange={(e) => setNewAccount((p) => ({ ...p, code: e.target.value }))}
              placeholder="e.g. SALARY"
              inputProps={{ maxLength: 30 }}
            />
            <TextField
              label="Account Name"
              value={newAccount.name}
              onChange={(e) => setNewAccount((p) => ({ ...p, name: e.target.value }))}
              placeholder="e.g. Salary Expense"
              inputProps={{ maxLength: 120 }}
            />
            <TextField
              select
              label="Type"
              value={newAccount.type}
              onChange={(e) => setNewAccount((p) => ({ ...p, type: e.target.value }))}
            >
              <MenuItem value="expense">Expense</MenuItem>
              <MenuItem value="asset">Asset</MenuItem>
              <MenuItem value="liability">Liability</MenuItem>
              <MenuItem value="equity">Equity</MenuItem>
              <MenuItem value="income">Income</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddAccountOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={creatingAccount}
            onClick={async () => {
              try {
                const created = await createAccount(newAccount).unwrap();
                setAddAccountOpen(false);
                setNewAccount({ code: '', name: '', type: 'expense' });
                if (created?.type === 'expense') {
                  setForm((prev) => ({ ...prev, accountId: created.id }));
                }
              } catch (err) {
                alert(err?.data?.message || 'Failed to create account');
              }
            }}
          >
            {creatingAccount ? 'Saving...' : 'Save Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Expenses;

